import React from "react";
import styled from "styled-components";

import { Button } from "@material-ui/core";
import { Close as CloseIcon, ViewModule as ViewModuleIcon } from "@material-ui/icons";

import { stopPropagation } from "../helpers/stopPropagation";

import Thumbnail from "./Thumbnail";

const Container = styled.div.attrs(({ visible }) => ({
    style: {
        bottom: visible ? "0" : "-100%"
    }
}))`
    width: 100vw;
    height: 100vh;
    background: black;
    position: absolute;
    left: 0;
    z-index: 999999;

    transition: bottom ease-in-out 0.5s;
`;

const ThumbnailsScrolledContainer = styled.div`
    margin: 48px 20px 20px 20px;
    max-height: calc(100% - 68px);
    overflow-y: auto;

    ::-webkit-scrollbar { 
        display: none;
    }
`;

const ThumbnailsContainer = styled.div`
    padding: 2px;
    display: grid;
    grid-auto-rows: min-content;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-column-gap: 20px;
    grid-row-gap: 40px;
    justify-items: center;
    margin-bottom: 40px;
`;

const IconContainer = styled.div`
    position: absolute;
    top: 12px;
    right: 50%;
    transform: translateX(50%);
    color: #ccc;
    cursor: pointer;
    display: flex;
    align-items: center;
`;

const IconText = styled.span`
    margin-left: 5px;
`;

const CloseButton = styled(CloseIcon)`
    position: absolute;
    right: 15px;
    top: 12px;
    color: #ccc;
    cursor: pointer;

    &:hover {
        color: #11a9e2;
    }
`;

function getStyledIconButton(Icon, style = {}) {
    return styled(Icon).attrs(() => ({ style }))`
      color: #ccc;
      cursor: pointer;

      &:hover {
        color: #11a9e2;
      }
    `;
}

const ThumbnailsButton = getStyledIconButton(ViewModuleIcon);

class ThumbnailsPane extends React.Component {
    constructor(props) {
        super(props);

        this.scrolledContainerRef = React.createRef();
        this.thumbnailsContainerRef = React.createRef();
        this.hilitedThumbnailIndex = null;
    }

    componentDidMount() {
        // The pane is mounted already open (isOpen is true on the first render),
        // so scroll the grid to the current slide as soon as it's laid out.
        this.scrollToCurrentSlide();
    }

    componentDidUpdate(prevProps) {
        // Handle the case where the pane is kept mounted and re-opened.
        if (this.props.isOpen && !prevProps.isOpen) {
            this.scrollToCurrentSlide();
        }
    }

    componentWillUnmount() {
        if (this.scrollRetryFrame) {
            cancelAnimationFrame(this.scrollRetryFrame);
        }
    }

    scrollToCurrentSlide = (attemptsLeft = 10) => {
        const scroller = this.scrolledContainerRef.current;
        const container = this.thumbnailsContainerRef.current;
        if (!scroller || !container || this.hilitedThumbnailIndex === null) {
            return;
        }

        const hilitedEl = container.children[this.hilitedThumbnailIndex];
        if (!hilitedEl) {
            return;
        }

        // Thumbnail heights (and therefore the scrollable extent) fill in over the
        // first few frames after mount as the grid lays out. Wait until the content
        // is taller than the viewport before scrolling, otherwise we'd center against
        // a collapsed grid and land near the top. Bail out after a bounded number of
        // retries so a deck that genuinely fits on screen doesn't loop forever.
        if (attemptsLeft > 0 && scroller.scrollHeight <= scroller.clientHeight) {
            this.scrollRetryFrame = requestAnimationFrame(() => this.scrollToCurrentSlide(attemptsLeft - 1));
            return;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const hilitedRect = hilitedEl.getBoundingClientRect();

        // Position of the hilited thumbnail's top within the scrollable content
        const offsetWithinContent = (hilitedRect.top - scrollerRect.top) + scroller.scrollTop;
        // Center the current slide vertically; clamping keeps the first/last slides
        // pinned to the top/bottom instead of overscrolling
        const targetScrollTop = offsetWithinContent - (scroller.clientHeight - hilitedRect.height) / 2;
        scroller.scrollTop = Math.max(0, Math.min(targetScrollTop, scroller.scrollHeight - scroller.clientHeight));
    };

    render() {
        const {
            isOpen,
            presentation,
            slides,
            slidesMetadata,
            currentSlideIndex,
            currentSlidePlaybackStageIndex,
            goToSlide,
            onClose
        } = this.props;

        const skippedSlideIds = Object.entries(slidesMetadata).map(([slideId, { isSkipped }]) => isSkipped ? slideId : null).filter(slideId => !!slideId);

        this.hilitedThumbnailIndex = null;
        const Thumbnails = [];
        presentation.getSips(skippedSlideIds)
            .forEach((slideId, slideIndex) => {
                const slide = slides[slideId];
                const playbackStagesCount = slide.getPlaybackStageCount();
                [...Array(playbackStagesCount)].forEach((_, playbackStageIndex) => {
                    const hilited = slideIndex === currentSlideIndex && playbackStageIndex === currentSlidePlaybackStageIndex;
                    if (hilited) {
                        this.hilitedThumbnailIndex = Thumbnails.length;
                    }
                    Thumbnails.push(
                        <Thumbnail
                            key={`${slideId}-${playbackStageIndex}`}
                            shouldLoadThumbnail={isOpen}
                            hilited={hilited}
                            presentation={presentation}
                            slide={slide}
                            slideMetadata={slidesMetadata[slideId]}
                            clickable={true}
                            playbackStageIndex={playbackStageIndex}
                            slideIndex={slideIndex}
                            onClick={event => stopPropagation(event, () => {
                                goToSlide(slideIndex, true, playbackStageIndex);
                                onClose();
                            })}
                        />
                    );
                });
            });

        return (
            <Container visible={isOpen} className="ThumbnailsPane">
                {!window.isZoomApp && <CloseButton onClick={event => stopPropagation(event, onClose)} />}
                <IconContainer>
                    {!window.isZoomApp && <ThumbnailsButton onClick={event => stopPropagation(event, onClose)} />}
                    <IconText>{Thumbnails.length} {Thumbnails.length > 1 ? "SLIDES" : "SLIDE"}</IconText>
                </IconContainer>
                <ThumbnailsScrolledContainer ref={this.scrolledContainerRef}>
                    <ThumbnailsContainer ref={this.thumbnailsContainerRef}>
                        {Thumbnails}
                    </ThumbnailsContainer>
                </ThumbnailsScrolledContainer>
            </Container >
        );
    }
}

export default ThumbnailsPane;
